import React, { useState, useEffect, CSSProperties } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useIsAuthenticated, useMsal } from '@azure/msal-react'

import { NavBar } from '@components/navigation/NavBar/NavBar';
import Button, { ButtonStyle } from '@components/buttons/Button/Button';
import { ConfirmationBox, WarningBox } from '@components/cards/messageBox';
import LoadingSpinner from '@components/spinners/Circular/Circular';
import { OnboardingService } from '@services/journeys/Onboarding';
import { Country } from '@utils/constants/localisation';
import ExternalLink from '@assets/images/external_link.svg';
import { PlanOptions } from '@utils/planOptions';
import { WarningItem } from '@services/ErrorTypes';
import Warnings from '@utils/Warnings';
import { warningAdditionalMessages } from '@utils/warningMessages';


type SuccessViewProps = {
    make: string;
    model: string;
    year: string;
    licencePlate: string;
    fleetioLink: string;
    zohoLink: string;
    email: string;
    plan: string;
    chargebeeSubsLink?: string;
    isFoV2?: boolean;
};

type SuccessPageProps = {
    data: SuccessViewProps;
    warnings: WarningItem[];
};


const ChargebeeSubscriptionSection = ({ plan, isFoV2 }: { plan: PlanOptions, isFoV2?: boolean }) => {

    const paragraphStyle = {
        marginTop: '1.5vh',
    };

    switch (plan) {
        case (PlanOptions.RAVCreditHire):
        case (PlanOptions.RAVFirstPartyHire):
        case (PlanOptions.RAVFirstPartyHireEV):
        case (PlanOptions.RAVCreditHireEV):
            return (<>
                <ConfirmationBox message="Chargebee subscription created" />
                <p style={paragraphStyle}>
                    A new subscription has been created in Chargebee for the customer.
                    The customer’s Chargebee email address has been updated to <strong>max.davies-long@splend.com</strong>.
                </p>
            </>);
        case (PlanOptions.RAVSharecover):
        case (PlanOptions.RAVIAG):
            return (<>
                <WarningBox message="Chargebee subscription pending" />
                <p style={paragraphStyle}>
                    An email notification will be sent to the RAV team, who will create this Chargebee subscription manually.
                </p>
            </>);
        default:
            if (isFoV2) {
                return (<>
                    <ConfirmationBox message="Chargebee subscription created" />
                    <p style={paragraphStyle}>
                        A new subscription has been created in Chargebee for the customer with <strong>unbilled charges</strong>.
                        Please click this link to check the invoice has been collected in full.
                    </p>
                </>);
            }
            return (<>
                <ConfirmationBox message="Chargebee subscription created" />
                <p style={paragraphStyle}>
                    A new subscription has been created in Chargebee for the customer with <strong>unbilled charges</strong>.
                    Please proceed to Chargebee to generate the customer's first invoice.
                </p>
            </>);
    };
};


export default function OnboardingResult() {
    const [searchParams] = useSearchParams();
    const key = searchParams.get('key') || searchParams.get('document_reference') || '';
    const documentType = searchParams.get('document_type');

    const { custId, country } = useParams() as { custId: string, country: Country, key: string };

    const navigate = useNavigate();
    const msalContext = useMsal();
    const isAuthenticated = useIsAuthenticated();

    const [responseReceived, setResponseReceived] = useState<boolean>(false);
    const [response, setResponse] = useState<any>();
    const [success, setSuccess] = useState<boolean>(false);
    const [redirect, setRedirect] = useState<boolean>(false);
    const IMSTokenParams = { msalContext, isAuthenticated };

    // Determine if this is a v2 journey (GB and AU Flexi Own and Pre-loved)
    const isV2Journey = documentType === 'flexi_own_v2_subscription_contract';

    let pageTitleText = success ? "Confirmation" : "Sorry, we ran into a problem";

    useEffect(
        () => {
            // Use REST endpoint for GB Flexi Own V2 and Pre-loved journeys
            const serviceCall = isV2Journey
                ? OnboardingService.createSubscription(custId, key, country, IMSTokenParams)
                : OnboardingService.postRedisKey(key, country, IMSTokenParams);

            serviceCall
                .then((resp) => {
                    setResponse(resp);
                    if (resp.data?.missingKey) {
                        setRedirect(true);
                    }
                    setResponseReceived(true);
                    setSuccess(true);
                }).catch(() => {
                    setResponseReceived(true);
                    setSuccess(false);
                })
        },
        []
    );

    useEffect(() => {
        if (redirect) {
            navigate('/');
        }
    });

    const showSubsSection = (subsLink: string, subsPlan: PlanOptions): boolean => {
        if (subsLink) return true;
        if (subsPlan == PlanOptions.RAVSharecover || subsPlan == PlanOptions.RAVIAG) return true;

        return false;
    }

    const sectionSpacingTop = { marginTop: '2.5vh' };

    const pageStyle: CSSProperties = {
        margin: "2vh 2.5vw",
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        height: 'calc(100vh - 100px)'
    };

    const buttonContainerStyle = {
        marginTop: 'auto',
    };

    const paragraphStyle = {
        marginTop: '1.5vh',
    };

    const SuccessPage = ({ data, warnings }: SuccessPageProps) => (<>

        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'space-between', marginBottom: '2.5vh' }}>
            <div>
                <div style={sectionSpacingTop}>
                    <ConfirmationBox message="Contract and and Zoho subscription created" testId="onboarding-result-zoho-confirmation"></ConfirmationBox>
                    <p style={paragraphStyle}>
                        A new subscription has been created in Zoho for the customer.
                        A signed contract PDF document has been generated and added to the customer subscription.
                        <a href={data.zohoLink} target="_blank" data-testid="onboarding-result-zoho-link">View subscription in Zoho.</a>
                    </p>
                    <p>A copy of the contract PDF has been emailed to <strong>{data.email}</strong>.</p>
                </div>
                <div style={sectionSpacingTop}>
                    <ConfirmationBox message="Fleetio assignment created" testId="onboarding-result-fleetio-confirmation"></ConfirmationBox>
                    <p style={paragraphStyle}>
                        The following vehicle has been assigned to the customer in Fleetio: <strong>{data.licencePlate} - {data.year} {data.make} {data.model}</strong>.
                        <a href={data.fleetioLink} target="_blank" data-testid="onboarding-result-fleetio-link">View in Fleetio.</a>
                    </p>
                </div>
                {showSubsSection(data.chargebeeSubsLink || "", data.plan as PlanOptions) &&
                    <div style={sectionSpacingTop}>
                        <ChargebeeSubscriptionSection plan={data.plan as PlanOptions} isFoV2={data.isFoV2} />
                    </div>
                }
            </div>

            <div style={sectionSpacingTop}>
                <div style={paragraphStyle}>
                    <Warnings errors={warnings} additionalMessages={warningAdditionalMessages} />
                </div>
            </div>

            {data.chargebeeSubsLink &&
                <Button
                    label={'View subscription in Chargebee'}
                    iconSrc={ExternalLink}
                    iconRight={true}
                    btnType={ButtonStyle.primary}
                    onClickFunc={() => window.open(data.chargebeeSubsLink, '_blank')}
                />
            }
        </div>
    </>);

    const FailurePage = () => (
        <>
            <WarningBox message="Something went wrong"></WarningBox>
            <p style={paragraphStyle}>We’re sorry, we were unable to process your request.</p>
            <p style={paragraphStyle}>Please try again later and if the issue persists, please log a ticket with IT Helpdesk.</p>
        </>
    );

    return (<>
        {/* This conditional rendering is used to ensure that the navbar does not display on the loading screen */}
        {responseReceived && <NavBar pageTitle={pageTitleText} showButton={false} />}

        <div style={pageStyle}>
            {responseReceived
                ? (success
                    ? <SuccessPage data={response.data} warnings={response.warnings} />
                    : <FailurePage />)
                : <div style={{ textAlign: 'center', lineHeight: '100vh' }}><LoadingSpinner /></div>
            }
            <div style={buttonContainerStyle}>
                <Button label="Customer overview"
                    onClickFunc={() => navigate(`/customer/${country}/${custId}/overview`)}
                    testId="onboarding-result-button"
                    btnType={ButtonStyle.secondary}
                ></Button>
            </div>
        </div>
    </>);
};
